import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Heart, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SubscriptionSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [checking, setChecking] = useState(true);
  const [success, setSuccess] = useState(false);
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus();
    } else {
      // No session ID means free trial - show success immediately
      setSuccess(true);
      setChecking(false);
    }
  }, [sessionId]);

  const pollPaymentStatus = async (attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setChecking(false);
      return;
    }

    try {
      const response = await axios.get(
        `${API}/subscription/checkout-status/${sessionId}`,
        { withCredentials: true }
      );

      if (response.data.payment_status === 'paid') {
        setSuccess(true);
        setChecking(false);
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
    }
  };

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f8f9fb]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-[#2563eb] mx-auto mb-4"></div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Processing your payment...</h2>
          <p className="text-gray-600">Please wait while we confirm your subscription</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#f0f7ff] to-white flex items-center justify-center px-6">
      <div className="max-w-md w-full text-center">
        {success ? (
          <>
            <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-12 h-12 text-green-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-4">Welcome to Premium!</h1>
            <p className="text-lg text-gray-600 mb-8">
              Your subscription is now active. You have full access to all features.
            </p>
            <Button
              onClick={() => navigate('/dashboard')}
              data-testid="go-to-dashboard-button"
              className="bg-[#2563eb] hover:bg-[#1e40af] text-white px-8 py-3 rounded-lg text-lg font-semibold"
            >
              Go to Dashboard
            </Button>
          </>
        ) : (
          <>
            <div className="w-20 h-20 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-6">
              <Heart className="w-12 h-12 text-amber-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-4">Processing...</h1>
            <p className="text-lg text-gray-600 mb-8">
              We're still processing your payment. Please check your email for confirmation.
            </p>
            <Button
              onClick={() => navigate('/dashboard')}
              data-testid="continue-button"
              className="bg-[#2563eb] hover:bg-[#1e40af] text-white px-8 py-3 rounded-lg text-lg font-semibold"
            >
              Continue
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

export default SubscriptionSuccess;
